/**
 * AccountModal.js
 * 
 * 檔案標頭註解：
 * 改版日期時間：2025-07-19 15:30:00
 * 版本：V1.02
 * 檔案主要功能與職責：帳號管理模態框，提供密碼修改、業績查詢、推播通知管理等功能
 * 這個檔案是做什麼的？它解決了什麼問題？
 * - 提供密碼修改功能
 * - 提供業績查詢功能（自訂範圍和本月查詢）
 * - 提供推播通知診斷和管理功能
 * - 支援管理員專用的推播修復功能
 * 
 * 版本更新說明：
 * V1.02 (2025-07-19) - 修正業績查詢每筆明細日期排序問題，按日期從新到舊排序
 * V1.01 (2025-01-16) - 初始版本，從 App.js 模組化搬移
 * 
 * 注意事項：
 * - 需要傳入 currentUser、db、bookings、appId 等參數
 * - 支援 Firebase 資料庫操作
 * - 支援推播通知診斷和修復
 * - 管理員專用功能需要 isAdmin 權限
 * 
 * 關連檔案：
 * - App.js (主要使用此模組)
 * - Firebase 認證和推播系統
 */

import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc, collection, getDocs, query, where, limit, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getToken } from 'firebase/messaging';

const AccountModal = ({ onClose, currentUser, db, appId, onResetPush }) => {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [salesResult, setSalesResult] = useState(null);
    const [pushDiagnostic, setPushDiagnostic] = useState(null);
    const [products, setProducts] = useState([]);
    const [productMap, setProductMap] = useState({});
    const [accountBookings, setAccountBookings] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [historyError, setHistoryError] = useState('');

    // 首頁只保留本月預約；使用者開啟帳號時才讀取自己的完整歷史。
    useEffect(() => {
        let isActive = true;

        const fetchAccountBookings = async () => {
            setIsLoadingHistory(true);
            setHistoryError('');
            try {
                const bookingsQuery = query(
                    collection(db, `artifacts/${appId}/public/data/bookings`),
                    where('vendorId', '==', currentUser.id)
                );
                const snapshot = await getDocs(bookingsQuery);
                if (isActive) {
                    setAccountBookings(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
                }
            } catch (err) {
                console.error('載入個人歷史預約失敗:', err);
                if (isActive) setHistoryError('歷史業績資料載入失敗，請稍後再試。');
            } finally {
                if (isActive) setIsLoadingHistory(false);
            }
        };

        if (db && appId && currentUser?.id) fetchAccountBookings();
        return () => { isActive = false; };
    }, [db, appId, currentUser?.id]);

    // 診斷推播通知狀態
    const diagnosePushNotification = async () => {
        const diagnostic = {
            browserSupport: 'Notification' in window && 'serviceWorker' in navigator,
            permission: Notification.permission,
            serviceWorkerRegistered: false,
            fcmToken: null,
            localRegistered: false,
            firestoreCollection: 'unknown'
        };

        try {
            // 檢查 Service Worker
            if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.getRegistration();
                diagnostic.serviceWorkerRegistered = !!registration;
            }

            // 檢查 FCM Token
            try {
                if (window.messaging) {
                    const token = await getToken(window.messaging, {
                        vapidKey: 'BMvAspKI4uTAeaGge_dDWsf-YcSzhbM7iNy6f9dgx71UFlG1NFJLpsTFShOtIOBgwh-R00Mp-1VF77j1NRLAR50'
                    });
                    diagnostic.fcmToken = token ? 'exists' : 'missing';
                    if (token) {
                        diagnostic.tokenPreview = token.substring(0, 20) + '...';
                    }
                } else {
                    // 嘗試重新初始化 messaging
                    console.log('🚀 window.messaging 不存在，嘗試重新初始化...');
                    const { getMessaging } = await import('firebase/messaging');
                    const messaging = getMessaging();
                    const token = await getToken(messaging, {
                        vapidKey: 'BMvAspKI4uTAeaGge_dDWsf-YcSzhbM7iNy6f9dgx71UFlG1NFJLpsTFShOtIOBgwh-R00Mp-1VF77j1NRLAR50'
                    });
                    diagnostic.fcmToken = token ? 'exists' : 'missing';
                    if (token) {
                        diagnostic.tokenPreview = token.substring(0, 20) + '...';
                    }
                }
            } catch (e) {
                console.error('FCM Token 檢測失敗:', e);
                diagnostic.fcmToken = 'error: ' + e.message;
            }

            // 檢查本地記錄
            const registeredKey = `fcmTokenRegistered_${currentUser.id}`;
            diagnostic.localRegistered = localStorage.getItem(registeredKey) === 'yes';

            // 檢查 Firestore 集合狀態
            try {
                const fcmTokensRef = collection(db, `artifacts/${appId}/fcmTokens`);
                const snapshot = await getDocs(query(fcmTokensRef, limit(1)));
                diagnostic.firestoreCollection = snapshot.size > 0 ? 'exists' : 'empty';
            } catch (e) {
                diagnostic.firestoreCollection = 'error: ' + e.message;
            }

        } catch (error) {
            console.error('診斷過程發生錯誤:', error);
        }

        setPushDiagnostic(diagnostic);
    };

    // 強制重新註冊推播通知
    const forceReregisterPush = async () => {
        console.log('🔧 開始強制重新註冊推播通知...');

        try {
            // 清除本地記錄
            const registeredKey = `fcmTokenRegistered_${currentUser.id}`;
            localStorage.removeItem(registeredKey);
            console.log('🗑️ 已清除本地註冊記錄');

            // 檢查基本條件
            if (!('Notification' in window)) {
                throw new Error('瀏覽器不支援推播通知');
            }

            if (!('serviceWorker' in navigator)) {
                throw new Error('瀏覽器不支援 Service Worker');
            }

            // 請求推播權限
            console.log('🔔 請求推播權限...');
            const permission = await Notification.requestPermission();
            console.log('�� 權限結果:', permission);

            if (permission !== 'granted') {
                throw new Error('推播權限被拒絕，請在瀏覽器設定中允許通知');
            }

            // 重新初始化 messaging
            console.log('🚀 重新初始化 Firebase Messaging...');
            const { getMessaging } = await import('firebase/messaging');
            const messaging = getMessaging();
            console.log('✅ Firebase Messaging 初始化完成');

            // 取得 FCM Token
            console.log('🎯 取得 FCM Token...');
            const currentToken = await getToken(messaging, {
                vapidKey: 'BMvAspKI4uTAeaGge_dDWsf-YcSzhbM7iNy6f9dgx71UFlG1NFJLpsTFShOtIOBgwh-R00Mp-1VF77j1NRLAR50'
            });

            if (!currentToken) {
                throw new Error('無法取得 FCM Token，請檢查網路連線或稍後再試');
            }

            console.log('🎯 FCM Token 取得成功:', currentToken.substring(0, 20) + '...');

            // 儲存到 Firestore
            const tokenData = {
                userId: currentUser.id,
                timestamp: serverTimestamp(),
                userAgent: navigator.userAgent,
                createdAt: new Date().toISOString(),
                deviceType: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop',
                reregistered: true,
                reregisteredAt: new Date().toISOString()
            };

            console.log('💾 儲存 FCM Token 到 Firestore...');
            await setDoc(doc(db, 'artifacts', appId, 'fcmTokens', currentToken), tokenData);
            console.log('✅ FCM Token 已儲存到 Firestore');

            // 驗證儲存成功
            const savedDoc = await getDoc(doc(db, 'artifacts', appId, 'fcmTokens', currentToken));
            if (!savedDoc.exists()) {
                throw new Error('FCM Token 儲存失敗，請檢查網路連線');
            }
            console.log('✅ 驗證：FCM Token 確實已儲存');

            // 設定本地記錄
            localStorage.setItem(registeredKey, 'yes');

            alert('✅ 推播通知重新註冊成功！fcmTokens 集合已重建。');

            // 重新診斷
            diagnosePushNotification();

        } catch (error) {
            console.error('❌ 重新註冊失敗:', error);
            alert(`❌ 重新註冊失敗: ${error.message}\n\n請嘗試：\n1. 重新整理頁面\n2. 檢查網路連線\n3. 確認瀏覽器允許通知`);
        }
    };

    // 取得商品清單
    useEffect(() => {
        const fetchProducts = async () => {
            const productsCol = collection(db, `artifacts/${appId}/public/data/products`);
            const snapshot = await getDocs(productsCol);
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProducts(list);
            const map = {};
            list.forEach(p => { map[p.id] = p.name || p.title || p.id; });
            setProductMap(map);
        };
        fetchProducts();
    }, [db, appId]);

    // 用 useMemo 產生明細與加總，只顯示當前用戶的資料
    const salesDetailsList = useMemo(() => {
        if (!currentUser) return [];
        let details = [];
        accountBookings.forEach(data => {
            const date = data.date || '';
            const market = data.marketName || '';
            if (Array.isArray(data.salesDetails) && data.salesDetails.length > 0) {
                data.salesDetails.forEach(item => {
                    const pid = item.productId || 'unknown';
                    const qty = Number(item.quantity) || 0;
                    details.push({ date, market, productId: pid, quantity: qty });
                });
            } else if (data.salesQuantity) {
                const pid = 'shoe';
                const qty = Number(data.salesQuantity) || 0;
                details.push({ date, market, productId: pid, quantity: qty });
            }
        });
        // 【AI修正】按日期排序，從新到舊顯示
        // Why: 讓用戶能優先看到最新的業績記錄
        return details.sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [accountBookings, currentUser]);

    const salesSummary = useMemo(() => {
        const summary = {};
        salesDetailsList.forEach(row => {
            summary[row.productId] = (summary[row.productId] || 0) + row.quantity;
        });
        return Object.entries(summary).map(([pid, qty]) => ({ productId: pid, quantity: qty }));
    }, [salesDetailsList]);

    const handleChangePassword = async () => {
        setError('');
        setSuccess('');
        if (currentUser.password !== oldPassword) {
            return setError('舊密碼不正確！');
        }
        if (!newPassword || newPassword !== confirmPassword) {
            return setError('新密碼不能為空，且兩次輸入必須相同！');
        }
        try {
            const vendorRef = doc(db, `artifacts/${appId}/public/data/vendors`, currentUser.id);
            await updateDoc(vendorRef, { password: newPassword });
            setSuccess('密碼更新成功！');
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setError('密碼更新失敗，請稍後再試。');
        }
    };

    const calculateSales = (start, end) => {
        // 【AI修正】統一使用 salesDetails 計算總銷量，確保與明細表格數據一致
        const userBookings = accountBookings.filter(b =>
            new Date(b.date) >= start && 
            new Date(b.date) <= end
        );
        
        let totalSales = 0;
        userBookings.forEach(booking => {
            if (Array.isArray(booking.salesDetails) && booking.salesDetails.length > 0) {
                // 使用 salesDetails 計算總銷量
                booking.salesDetails.forEach(item => {
                    totalSales += Number(item.quantity) || 0;
                });
            } else if (booking.salesQuantity) {
                // 如果沒有 salesDetails，則使用 salesQuantity
                totalSales += Number(booking.salesQuantity) || 0;
            }
        });
        
        setSalesResult(`從 ${start.toLocaleDateString()} 到 ${end.toLocaleDateString()}，總銷量為: ${totalSales} 件`);
    };

    const handleCustomQuery = () => {
        if (startDate && endDate) {
            calculateSales(new Date(startDate), new Date(endDate));
        }
    };

    const handleThisMonthQuery = () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        calculateSales(start, end);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto relative" onClick={e => e.stopPropagation()}>
                {/* 右上角 X 關閉按鈕 */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="關閉"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                
                <h2 className="text-2xl font-bold mb-6 text-center pr-8">我的帳號</h2>
                <div className="space-y-4">
                    <details>
                        <summary className="font-semibold cursor-pointer">修改密碼</summary>
                        <div className="pt-2 space-y-2">
                            <input
                                type="password"
                                value={oldPassword}
                                onChange={e => setOldPassword(e.target.value)}
                                placeholder="請輸入舊密碼"
                                className="w-full p-2 border rounded-lg"
                            />
                            <input
                                type="password"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                placeholder="請輸入新密碼"
                                className="w-full p-2 border rounded-lg"
                            />
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                placeholder="再次確認新密碼"
                                className="w-full p-2 border rounded-lg"
                            />
                            {error && <p className="text-red-500 text-center text-sm">{error}</p>}
                            {success && <p className="text-green-500 text-center text-sm">{success}</p>}
                            <button
                                onClick={handleChangePassword}
                                className="w-full bg-green-500 text-white font-bold py-2 rounded-lg"
                            >
                                儲存新密碼
                            </button>
                        </div>
                    </details>

                    <details open>
                        <summary className="font-semibold cursor-pointer">業績查詢</summary>
                        <div className="pt-2 space-y-3">
                            {isLoadingHistory && <p className="text-sm text-gray-500">正在載入完整歷史業績…</p>}
                            {historyError && <p className="text-sm text-red-500">{historyError}</p>}
                            <div className="flex gap-2 items-center">
                                <label className="text-sm flex-shrink-0">自訂範圍:</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={e => setStartDate(e.target.value)}
                                    className="w-full p-1 border rounded-lg"
                                />
                                <span className="px-1">至</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={e => setEndDate(e.target.value)}
                                    className="w-full p-1 border rounded-lg"
                                />
                            </div>
                            <button
                                onClick={handleCustomQuery}
                                className="w-full bg-blue-500 text-white font-bold py-2 rounded-lg"
                            >
                                查詢自訂銷量
                            </button>
                            <button
                                onClick={handleThisMonthQuery}
                                className="w-full bg-indigo-500 text-white font-bold py-2 rounded-lg mt-2"
                            >
                                查詢本月銷量
                            </button>
                            {salesResult && (
                                <p className="text-center font-bold mt-3 p-2 bg-yellow-100 rounded-lg">
                                    {salesResult}
                                </p>
                            )}

                            {/* 新增：每筆明細表格 */}
                            <div className="mt-4">
                                <h4 className="font-semibold mb-1">每筆明細</h4>
                                <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gray-50">
                                                <th className="p-1">日期</th>
                                                <th className="p-1">市場</th>
                                                <th className="p-1">商品名稱</th>
                                                <th className="p-1">數量</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isLoadingHistory ? (
                                                <tr><td colSpan={4} className="text-center text-gray-400">載入中…</td></tr>
                                            ) : salesDetailsList.length === 0 ? (
                                                <tr><td colSpan={4} className="text-center text-gray-400">無資料</td></tr>
                                            ) : salesDetailsList.map((row, i) => (
                                                <tr key={i}>
                                                    <td className="p-1">{row.date}</td>
                                                    <td className="p-1">{row.market}</td>
                                                    <td className="p-1">{productMap[row.productId] || row.productId}</td>
                                                    <td className="p-1">{row.quantity}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            {/* 新增：商品加總表格 */}
                            <div className="mt-4">
                                <h4 className="font-semibold mb-1">商品加總</h4>
                                <div style={{ maxHeight: '100px', overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gray-50">
                                                <th className="p-1">商品名稱</th>
                                                <th className="p-1">總銷量</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isLoadingHistory ? (
                                                <tr><td colSpan={2} className="text-center text-gray-400">載入中…</td></tr>
                                            ) : salesSummary.length === 0 ? (
                                                <tr><td colSpan={2} className="text-center text-gray-400">無資料</td></tr>
                                            ) : salesSummary.map((row, i) => (
                                                <tr key={i}>
                                                    <td className="p-1">{productMap[row.productId] || row.productId}</td>
                                                    <td className="p-1">{row.quantity}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </details>

                    <details>
                        <summary className="font-semibold cursor-pointer">推播通知管理</summary>
                        <div className="pt-2 space-y-2">
                            <button
                                onClick={diagnosePushNotification}
                                className="w-full bg-blue-500 text-white font-bold py-2 rounded-lg"
                            >
                                🔍 診斷推播狀態
                            </button>

                            {pushDiagnostic && (
                                <div className="mt-3 p-3 bg-gray-100 rounded-lg text-sm">
                                    <h4 className="font-bold mb-2">推播診斷結果：</h4>
                                    <div className="space-y-1">
                                        <p>🌐 瀏覽器支援: {pushDiagnostic.browserSupport ? '✅ 是' : '❌ 否'}</p>
                                        <p>🔔 通知權限: {
                                            pushDiagnostic.permission === 'granted' ? '✅ 已允許' :
                                            pushDiagnostic.permission === 'denied' ? '❌ 已拒絕' :
                                            '⚠️ 未設定'
                                        }</p>
                                        <p>⚙️ Service Worker: {pushDiagnostic.serviceWorkerRegistered ? '✅ 已註冊' : '❌ 未註冊'}</p>
                                        <p>🎯 FCM Token: {pushDiagnostic.fcmToken === 'exists' ? '✅ 存在' : pushDiagnostic.fcmToken === 'missing' ? '❌ 不存在' : '⚠️ ' + pushDiagnostic.fcmToken}</p>
                                        {pushDiagnostic.tokenPreview && <p>🔑 Token 預覽: {pushDiagnostic.tokenPreview}</p>}
                                        <p>💾 本地記錄: {pushDiagnostic.localRegistered ? '✅ 已註冊' : '❌ 未註冊'}</p>
                                        {pushDiagnostic.firestoreCollection && <p>🗂️ Firestore 集合: {pushDiagnostic.firestoreCollection === 'empty' ? '❌ 空的' : pushDiagnostic.firestoreCollection === 'exists' ? '✅ 存在' : '⚠️ ' + pushDiagnostic.firestoreCollection}</p>}
                                        {pushDiagnostic.error && <p className="text-red-600">❌ 錯誤: {pushDiagnostic.error}</p>}

                                        {/* 修復按鈕 - 只有管理員可見 */}
                                        {currentUser?.isAdmin && (pushDiagnostic.firestoreCollection === 'empty' || (pushDiagnostic.fcmToken !== 'exists' && pushDiagnostic.fcmToken !== null)) && (
                                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                                {pushDiagnostic.firestoreCollection === 'empty' ? (
                                                    <p className="text-red-800 text-sm mb-2">⚠️ 檢測到 fcmTokens 集合是空的！這是推播無法運作的原因。</p>
                                                ) : (
                                                    <p className="text-red-800 text-sm mb-2">⚠️ 檢測到 FCM Token 無法取得！這是推播無法運作的原因。</p>
                                                )}
                                                <button
                                                    onClick={forceReregisterPush}
                                                    className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-red-700"
                                                >
                                                    🔧 立即修復推播通知
                                                </button>
                                                <p className="text-xs text-gray-500 mt-2">* 管理員專用功能</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <button
                                onClick={() => { onClose(); onResetPush(); }}
                                className="w-full bg-orange-500 text-white font-bold py-2 rounded-lg hover:bg-orange-600 transition"
                            >
                                🔔 重新註冊推播通知
                            </button>
                            <p className="text-xs text-gray-500 text-center">如果推播通知有問題，可以重新註冊</p>
                        </div>
                    </details>
                </div>
            </div>
        </div>
    );
};

export default AccountModal;
